import React, { useMemo } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import useYamlData from '../hooks/useYamlData';
import CultureCard from '../components/CultureCard';
import '../styles/Cultures.css';

/*
 * The nine cultures, in orbit order rather than file order.
 *
 * Day-inward: the wayfinders closest to the star, the nightwatch furthest out
 * in the cold. That is the same ordering the orrery uses, so reading down this
 * page walks the system from the inside out.
 */
const Cultures = () => {
  const { data, loading, error } = useYamlData('/data/in-practice.yaml');

  const ordered = useMemo(
    () => [...(data?.hats ?? [])].sort((a, b) => b.diurnal - a.diurnal),
    [data],
  );

  if (loading) return <p className="tmc-state">Loading cultures...</p>;
  if (error) return <p className="tmc-state tmc-state--error">{error}</p>;

  const day = ordered.filter((hat) => hat.diurnal > 0.5);
  const night = ordered.filter((hat) => hat.diurnal < 0.5);
  const boundary = ordered.find((hat) => hat.diurnal === 0.5);

  return (
    <div className="tmc-page page-shell cultures-page">
      <header className="tmc-page-header">
        <p className="eyebrow">The nine cultures</p>
        <h1>Nine roles, nine peoples, one system.</h1>
        <p className="tmc-lede">
          Every discipline the year needed, as a world with someone living on it. One number
          orders them: how much of the work is done facing people, and how much is done while
          nobody is watching. It decides the orbit, the sky and the light.
        </p>
      </header>

      <section className="culture-band" aria-labelledby="day-heading">
        <div className="tmc-section-head">
          <p className="eyebrow">Inner orbits</p>
          <h2 id="day-heading">Inner orbit</h2>
          <p className="tmc-lede">
            Close to the star, and graded by someone else. Their work is finished when a
            stranger can use it without being told how.
          </p>
        </div>
        <div className="culture-grid">
          {day.map((hat) => (
            <CultureCard hat={hat} key={hat.id} />
          ))}
        </div>
      </section>

      {boundary && (
        <section className="culture-transition" aria-labelledby="boundary-heading">
          <div className="tmc-section-head">
            <p className="eyebrow">Boundary orbit</p>
            <h2 id="boundary-heading">Where the orbits meet</h2>
            <p className="tmc-lede">
              AI sits between the inner and outer cultures: one world that coordinates a
              moving belt of agents without becoming another people in the system.
            </p>
          </div>
          <div className="culture-transition-grid">
            <CultureCard hat={boundary} wide />
          </div>
        </section>
      )}

      <section className="culture-band" aria-labelledby="night-heading">
        <div className="tmc-section-head">
          <p className="eyebrow">Outer orbits</p>
          <h2 id="night-heading">Outer orbit</h2>
          <p className="tmc-lede">
            Out in the cold, on long slow years. Nobody sees this work while it is going
            right, which is the only condition under which it is going right.
          </p>
        </div>
        <div className="culture-grid">
          {night.map((hat) => (
            <CultureCard hat={hat} key={hat.id} />
          ))}
        </div>
      </section>

      <nav className="practice-links" aria-label="Related pages">
        <Link to="/in-practice">
          <span>01</span>
          <strong>See the system in motion</strong>
          <ArrowUpRight aria-hidden="true" />
        </Link>
        <Link to="/career">
          <span>02</span>
          <strong>The career atlas</strong>
          <ArrowUpRight aria-hidden="true" />
        </Link>
      </nav>
    </div>
  );
};

export default Cultures;
